/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

export interface BreadcrumbSegment {
  label: string;
  onClick?: () => void;
  muted?: boolean;
  /** When true, the segment (and its preceding separator) is hidden below the `sm` breakpoint. */
  hideOnNarrow?: boolean;
  /** When set, the segment is rendered as an Avatar (logo image) with `label` as accessible fallback. */
  avatarUrl?: string;
  /**
   * Custom visual rendered before the label (e.g. an already-loaded logo image or an icon).
   * Unlike `avatarUrl`, the label stays visible at every breakpoint. Takes precedence over `avatarUrl`.
   */
  leadingIcon?: ReactNode;
  /**
   * An icon after the label, inside the same button — what says that tapping the
   * segment does something other than go back to it (e.g. the chevrons of a
   * block's page, whose name opens the list of the round's blocks).
   */
  trailingIcon?: ReactNode;
}

export interface BreadcrumbState {
  onBack: () => void;
  segments: BreadcrumbSegment[];
  loading?: boolean;
  /** When true, the DivePeaks home logo at the left of the navbar is hidden. */
  hideHomeLogo?: boolean;
  /**
   * When set, the DivePeaks home logo runs this instead of navigating home
   * (e.g. the feed fullscreen video uses it to close the overlay and return to the feed).
   */
  onHomeLogo?: () => void;
}

/**
 * The places in the navbar a page may draw into, through a portal (see
 * NavbarPortal): `actions`, at the head of the buttons on the right, and
 * `strip`, a full-width row under them, inside the sticky header, which takes
 * no room while empty.
 *
 * A portal rather than a node in the state above, so what is drawn there stays
 * part of the page that draws it — its state, its timers, and the contexts it
 * reads (a round's refresh generation, say) — and only its pixels move.
 */
export type NavbarSlotName = 'actions' | 'strip';
export type NavbarSlots = Record<NavbarSlotName, HTMLElement | null>;

interface BreadcrumbContextValue {
  breadcrumb: BreadcrumbState | null;
  setBreadcrumb: (state: BreadcrumbState | null) => void;
  /** Null until the navbar has mounted, and in an app that renders none. */
  navbarSlots: NavbarSlots;
  setNavbarSlot: (name: NavbarSlotName, node: HTMLElement | null) => void;
}

const NO_SLOTS: NavbarSlots = { actions: null, strip: null };

const BreadcrumbContext = createContext<BreadcrumbContextValue>({
  breadcrumb: null,
  setBreadcrumb: () => {},
  navbarSlots: NO_SLOTS,
  setNavbarSlot: () => {},
});

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [breadcrumb, setBreadcrumb] = useState<BreadcrumbState | null>(null);
  const [navbarSlots, setNavbarSlots] = useState<NavbarSlots>(NO_SLOTS);
  const setNavbarSlot = useCallback((name: NavbarSlotName, node: HTMLElement | null) => {
    setNavbarSlots((current) => (current[name] === node ? current : { ...current, [name]: node }));
  }, []);

  const value = useMemo(
    () => ({ breadcrumb, setBreadcrumb, navbarSlots, setNavbarSlot }),
    [breadcrumb, navbarSlots, setNavbarSlot]
  );

  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>;
}

export function useBreadcrumbContext() {
  return useContext(BreadcrumbContext);
}
